import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MinHeapEngine from "../AdvanceTreesEngine/MinHeapEngine";
import styles from "../Advancetrees.module.css";

type Traversal = "preorder" | "inorder" | "postorder" | "levelorder";

const MinHeapWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [heapData, setHeapData] = useState<number[]>([]);
  const [insertValue, setInsertValue] = useState<number | "">("");
  const [traversal, setTraversal] = useState<Traversal | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<number[]>([]);
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);
  const [generateTrigger, setGenerateTrigger] = useState<number>(0);
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [activeTraversal, setActiveTraversal] = useState<Traversal | null>(null);
  const [traversalResult, setTraversalResult] = useState<number[]>([]);

  const pseudocodeMap: Record<Traversal, string[]> = {
    preorder: [
      "function preorder(node):",
      "    if node is null: return",
      "    visit(node)",
      "    preorder(node.left)",
      "    preorder(node.right)",
    ],
    inorder: [
      "function inorder(node):",
      "    if node is null: return",
      "    inorder(node.left)",
      "    visit(node)",
      "    inorder(node.right)",
    ],
    postorder: [
      "function postorder(node):",
      "    if node is null: return",
      "    postorder(node.left)",
      "    postorder(node.right)",
      "    visit(node)",
    ],
    levelorder: [
      "function levelorder(root):",
      "    if root is null: return",
      "    queue = [root]",
      "    while queue not empty:",
      "        node = queue.shift()",
      "        visit(node)",
      "        if node.left: queue.push(node.left)",
      "        if node.right: queue.push(node.right)",
    ],
  };

  const generateRandomHeap = () =>
    Array.from({ length: 7 }, () => Math.floor(Math.random() * 90) + 10);

  const handleGenerate = () => {
    setHeapData(generateRandomHeap());
    setTraversal(null);
    setActiveTraversal(null);
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
    setGenerateTrigger((p) => p + 1);
  };

  const handleInsert = () => {
    if (insertValue === "" || isNaN(Number(insertValue))) return;
    setHeapData((prev) => [...prev, Number(insertValue)]);
    setInsertValue("");
    setTraversal(null);
    setActiveTraversal(null);
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
    setGenerateTrigger((p) => p + 1);
  };

  const handleRemoveRoot = () => {
    setHeapData((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[0] = next[next.length - 1];
      next.pop();
      return next;
    });
    setTraversal(null);
    setActiveTraversal(null);
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
    setGenerateTrigger((p) => p + 1);
  };

  const handleTraversal = (type: Traversal) => {
    if (heapData.length === 0) return; 
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
    setActiveTraversal(type);
    setTraversal(type);
  };

  const handleReset = () => {
    setTraversal(null);
    setActiveTraversal(null);
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
    setResetTrigger((prev) => !prev);
  };

  const pseudoLines =
    activeTraversal ? pseudocodeMap[activeTraversal] : null;

  const traversalTitle =
    activeTraversal === null
      ? ""
      : activeTraversal === "levelorder"
      ? "Level Order (BFS)"
      : `${activeTraversal.charAt(0).toUpperCase()}${activeTraversal.slice(
          1
        )} Traversal`;

  return (
    <div className={styles.advTreeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Min Heap Visualization</h1>

      <p className={styles.description}>
        A <strong>Min Heap</strong> is a complete binary tree where each parent is smaller than its
        children. The smallest element always lives at the root. Insertions bubble up, and deletions
        bubble down to maintain the heap property.
      </p>

      <div className={styles.useCase}>
     <h2>Use Case:</h2>
         <p>
            Min Heaps are commonly used when you need to efficiently retrieve or remove the smallest element — such as in <strong>priority queues, Dijkstra’s shortest path, and heap-based sorting algorithms.</strong>
        </p>
     </div>

     <div className={styles.timeComplexity}>
       <h2>Time Complexity:</h2>
       <p>
           Both Insert and Remove are <strong> O(log n)</strong>
       </p>
        <i>
            note: <strong>Reminder - Heaps are not typically traversed. It is here for visualization and education purposes.</strong>
        </i>
      </div>

      <div className={styles.controls}>

        <div className={styles.inputGroup}>
          <input
            type="number"
            placeholder="Insert value"
            value={insertValue}
            onChange={(e) =>
              setInsertValue(e.target.value === "" ? "" : Number(e.target.value))
            }
            className={styles.inputField}
          />
          <button onClick={handleInsert}>Insert</button>
        </div>
        <button onClick={handleRemoveRoot}>Remove Root</button>
        <button onClick={handleGenerate}>Generate Heap</button>
        <button onClick={() => handleTraversal("preorder")}>Preorder</button>
        <button onClick={() => handleTraversal("inorder")}>Inorder</button>
        <button onClick={() => handleTraversal("postorder")}>Postorder</button>
        <button onClick={() => handleTraversal("levelorder")}>Level Order</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.vizContainer}>
        <MinHeapEngine
          heapData={heapData}
          traversal={traversal}
          setHighlightLine={setHighlightLine}
          highlightedNodes={highlightedNodes}
          setHighlightedNodes={setHighlightedNodes}
          resetTrigger={resetTrigger}
          generateTrigger={generateTrigger}
          setActiveLine={setActiveLine}
          setTraversalResult={setTraversalResult}
        />
      </div>
      {traversalResult.length > 0 && (
          <div className={styles.traversalResult}>
            <h3>Traversal Result:</h3>
              <p>
                {traversalResult.map((val, idx) => (
                  <span key={idx}>
                    {val}
                    {idx < traversalResult.length - 1 && " \u2192 "}
                  </span>
                ))}
              </p>
          </div>
        )}

      {pseudoLines && (
        <>
          <div className={styles.pseudoContainer}>
            <h3>Pseudocode: {traversalTitle}</h3>
            <pre>
              {pseudoLines.map((line, index) => (
                <code
                  key={index}
                  className={activeLine === index ? styles.highlightCode : ""}
                >
                  {line}
                </code>
              ))}
            </pre>
          </div>


        </>
      )}
    </div>
  );
};

export default MinHeapWrapper;


